import React, {useEffect, useMemo, useState} from 'react'
import {TimeSheet, DriverTimeSheet, TimesheetLunchBreak, UserRoles, WorkOrder, PartialUser} from "../../types";
import {
    deleteTimeSheet,
    deleteDriverTimeSheet,
    getTimesheets,
    getDriverTimesheets,
    getWorkOrders,
    getUsers,
    getLunchBreaks,
    deleteLunchBreak,
} from "../../api/dataService";
import {Navigate} from "react-router-dom";
import StackedContentContainer from "../ContentContainer";
import {ColumnDef} from "@tanstack/react-table";
import SearchableTable from "../SearchableTable";
import {checkIsAllowed, getDateString, getElapsedHours, getTimeString, getTotalEventHours, isLoadingSpinner} from "../../util/Helpers";
import {Alert, Button, ButtonGroup, Modal, Form, Dropdown} from "react-bootstrap"
import Offcanvas from 'react-bootstrap/Offcanvas';
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/reduxTypes";
import CreateTimeEntry from "../CreateTimeEntry";
import {endDateFilter, startDateFilter} from "../../util/CustomFilters";
import ExportTimecard from "../ExportTimecard";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths } from "date-fns";
import AsyncSelect from "react-select/async";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AddLunchBreakModal from "../AddLunchBreakModal";

const parentStyle = { padding: '2rem' };
const dateCellStyle: React.CSSProperties = {
    display: 'inline-block',
    minWidth: '7.5rem',
    whiteSpace: 'nowrap',
};

const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Custom input for DatePicker: readOnly prevents the mobile keyboard from appearing
// while react-datepicker still injects its own onClick to open the calendar.
const ReadOnlyDateInput = React.forwardRef<HTMLInputElement, React.HTMLProps<HTMLInputElement>>(
    (props, ref) => <input {...props} readOnly ref={ref} />
);

type ViewMode = "day" | "week" | "month" | "all";

type EntryKind = 'regular' | 'driver' | 'lunch_break';

// DisplayRow extends TimeSheet/DriverTimeSheet with optional lunch-break discriminator fields
type DisplayRow =
    | (TimeSheet & { _rowType: 'regular' })
    | (DriverTimeSheet & { _rowType: 'driver' })
    | (TimeSheet & {
        _rowType: 'lunch_break';
        _lunchBreakDuration?: number;
        _lunchBreakDate?: string;
        _lunchBreakStartTime?: string;
        _lunchBreakEndTime?: string;
    });

function ManageTimeEntriesPage() {
    const [timesheetList, setTimesheetList] = useState<TimeSheet[]>([]);
    const [driverTimesheetList, setDriverTimesheetList] = useState<DriverTimeSheet[]>([]);
    const [lunchBreakList, setLunchBreakList] = useState<TimesheetLunchBreak[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [workOrderList, setWorkOrderList] = useState<WorkOrder[]>([]);
    const [userList, setUserList] = useState<PartialUser[]>([]);

    const [showExportModal, setShowExportModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showLunchBreakModal, setShowLunchBreakModal] = useState(false);

    const [selectedEntryToUpdate, setSelectedEntryToUpdate] = useState<DisplayRow | null>(null);
    const [selectedEntryToDelete, setSelectedEntryToDelete] = useState<DisplayRow | null>(null);
    const [createEntryKind, setCreateEntryKind] = useState<'regular' | 'driver'>('regular');
    const [selectedLunchBreakToDelete, setSelectedLunchBreakToDelete] = useState<TimesheetLunchBreak | null>(null);
    const [selectedLunchBreakToEdit, setSelectedLunchBreakToEdit] = useState<TimesheetLunchBreak | null>(null);

    const user = useSelector((state: RootState) => state.user);

    // Permissions
    const isLabourer = useMemo(() => checkIsAllowed(user.groups, [UserRoles.labourer]), [user.groups]);

    const canAddDriverTime = useMemo(
        () =>
            checkIsAllowed(user.groups, [
                UserRoles.ownership,
                UserRoles.management,
                UserRoles.dispatch,
                UserRoles.driver,
                UserRoles.officeAdmin,
                UserRoles.sales,
            ]),
        [user.groups]
    );

    const canViewOthers = useMemo(
        () =>
            checkIsAllowed(user.groups, [
                UserRoles.ownership,
                UserRoles.management,
                UserRoles.officeAdmin,
                UserRoles.supervisor,
                UserRoles.dispatch,
                UserRoles.sales,
            ]),
        [user.groups]
    );

    const canViewAllUsers = useMemo(
        () =>
            checkIsAllowed(user.groups, [
                UserRoles.ownership,
                UserRoles.management,
                UserRoles.officeAdmin,
                UserRoles.dispatch,
            ]),
        [user.groups]
    );

    const canEditEmployee = useMemo(
        () =>
            checkIsAllowed(user.groups, [
                UserRoles.ownership,
                UserRoles.management,
                UserRoles.officeAdmin,
            ]),
        [user.groups]
    );

    const today = useMemo(() => toMidnight(new Date()), []);

    const [viewMode, setViewMode] = useState<ViewMode>("day");
    const [selectedDate, setSelectedDate] = useState<Date>(() => toMidnight(new Date()));
    const [selectedMember, setSelectedMember] = useState<number | "all">(() => Number(user.userid));
    const [successBanner, setSuccessBanner] = useState<string | null>(null);

    useEffect(() => {
        if (!successBanner) return;
        const timer = setTimeout(() => setSuccessBanner(null), 6000);
        return () => clearTimeout(timer);
    }, [successBanner]);

    // Ensure selectedMember defaults to logged in user when they lose permission or if user changes
    useEffect(() => {
        if (!canViewOthers) {
            setSelectedMember(Number(user.userid));
        } else {
            setSelectedMember((prev) => (prev === undefined || prev === null ? Number(user.userid) : prev));
        }
    }, [canViewOthers, user.userid]);

    // Fetch work orders once
    useEffect(() => {
        getWorkOrders()
            .then((workOrderResponse) => {
                if ('redirectTo' in workOrderResponse) {
                    return (<Navigate to={workOrderResponse.redirectTo} />);
                }
                if (Array.isArray(workOrderResponse)) {
                    setWorkOrderList(workOrderResponse);
                }
            })
            .catch((error) => console.error("Error fetching work orders:", error));
    }, []);

    // Fetch members list only if needed
    useEffect(() => {
        if (!canViewOthers) return;

        getUsers(undefined, '?is_active=true')
            .then((userResponse) => {
                if ('redirectTo' in userResponse) {
                    return (<Navigate to={userResponse.redirectTo} />);
                }
                if (Array.isArray(userResponse)) {
                    setUserList(userResponse);
                }
            })
            .catch((error) => console.error("Error fetching users:", error));
    }, [canViewOthers]);

    // Fetch timesheets (regular + driver) and lunch breaks when member scope changes (or on mount)
    useEffect(() => {
        setIsLoading(true);

        const effectiveMember = canViewOthers ? selectedMember : user.userid;
        const filters =
            effectiveMember === "all"
                ? ''
                : `?employee=${effectiveMember}`;

        // Breaks are restricted to the logged-in user unless they have canEditEmployee permission
        const effectiveBreakMember = canEditEmployee ? selectedMember : user.userid;
        const breakFilters =
            effectiveBreakMember === "all"
                ? ''
                : `?employee=${effectiveBreakMember}`;

        Promise.all([
            getTimesheets(undefined, filters),
            getDriverTimesheets(undefined, filters),
            getLunchBreaks(undefined, breakFilters),
        ])
            .then(([timesheetResponse, driverTimesheetResponse, lunchBreakResponse]) => {
                if ('redirectTo' in timesheetResponse) {
                    return (<Navigate to={(timesheetResponse as any).redirectTo} />);
                }
                setTimesheetList(Array.isArray(timesheetResponse) ? timesheetResponse : []);
                if (Array.isArray(driverTimesheetResponse)) {
                    setDriverTimesheetList(driverTimesheetResponse.filter((entry) => entry.status !== 'deleted'));
                } else {
                    setDriverTimesheetList([]);
                }
                setLunchBreakList(Array.isArray(lunchBreakResponse) ? lunchBreakResponse : []);
            })
            .catch((error) => console.error("Error fetching timesheet data:", error))
            .finally(() => setIsLoading(false));
    }, [selectedMember, canViewOthers, user.userid]);

    // Range for filtering by viewMode
    const range = useMemo(() => {
        if (viewMode === "all") return null;

        if (viewMode === "day") {
            const start = toMidnight(selectedDate);
            const end = addDays(start, 1);
            return { start, end };
        }

        if (viewMode === "week") {
            const start = toMidnight(startOfWeek(selectedDate, { weekStartsOn: 1 }));
            const end = addDays(toMidnight(endOfWeek(selectedDate, { weekStartsOn: 1 })), 1);
            return { start, end };
        }

        const start = toMidnight(startOfMonth(selectedDate));
        const end = addDays(toMidnight(endOfMonth(selectedDate)), 1);
        return { start, end };
    }, [selectedDate, viewMode]);

    const visibleTimesheets = useMemo(() => {
        const byStartDateAsc = (a: TimeSheet, b: TimeSheet) => {
            const ta = new Date(a.start_date).getTime();
            const tb = new Date(b.start_date).getTime();
            if (isNaN(ta) && isNaN(tb)) return 0;
            if (isNaN(ta)) return 1;
            if (isNaN(tb)) return -1;
            return ta - tb;
        };

        const effectiveMember = canViewOthers ? selectedMember : user.userid;
        const list = effectiveMember === "all"
            ? timesheetList
            : timesheetList.filter((ts) => String(ts.employee?.id) === String(effectiveMember));

        if (viewMode === "all") return [...list].sort(byStartDateAsc);
        if (!range) return [];

        return list
            .filter((ts) => {
                const start = new Date(ts.start_date);
                if (isNaN(start.getTime())) return false;
                const t = start.getTime();
                return t >= range.start.getTime() && t < range.end.getTime();
            })
            .sort(byStartDateAsc);
    }, [timesheetList, viewMode, range, canViewOthers, selectedMember, user.userid]);

    const visibleDriverTimesheets = useMemo(() => {
        const byStartDateAsc = (a: DriverTimeSheet, b: DriverTimeSheet) => {
            const ta = new Date(a.start_date).getTime();
            const tb = new Date(b.start_date).getTime();
            if (isNaN(ta) && isNaN(tb)) return 0;
            if (isNaN(ta)) return 1;
            if (isNaN(tb)) return -1;
            return ta - tb;
        };

        const effectiveMember = canViewOthers ? selectedMember : user.userid;
        const list = effectiveMember === "all"
            ? driverTimesheetList
            : driverTimesheetList.filter((ts) => String(ts.employee?.id) === String(effectiveMember));

        if (viewMode === "all") return [...list].sort(byStartDateAsc);
        if (!range) return [];

        return list
            .filter((ts) => {
                const start = new Date(ts.start_date);
                if (isNaN(start.getTime())) return false;
                const t = start.getTime();
                return t >= range.start.getTime() && t < range.end.getTime();
            })
            .sort(byStartDateAsc);
    }, [driverTimesheetList, viewMode, range, canViewOthers, selectedMember, user.userid]);

    const visibleLunchBreaks = useMemo(() => {
        const effectiveBreakMember = canViewOthers ? selectedMember : user.userid;
        const list = effectiveBreakMember === "all"
            ? lunchBreakList
            : lunchBreakList.filter((lb) => String(lb.employee?.id) === String(effectiveBreakMember));

        if (viewMode === "all") return list;
        if (!range) return [];

        return list.filter((lb) => {
            const date = new Date(lb.date + 'T00:00:00');
            if (isNaN(date.getTime())) return false;
            const t = date.getTime();
            return t >= range.start.getTime() && t < range.end.getTime();
        });
    }, [lunchBreakList, viewMode, range, canViewOthers, selectedMember, user.userid]);

    // Merge regular, driver, and lunch break rows for the table, sorted by start_date
    const allDisplayRows = useMemo((): DisplayRow[] => {
        const regularRows: DisplayRow[] = visibleTimesheets.map((ts) => ({
            ...ts,
            _rowType: 'regular',
        }));

        const driverRows: DisplayRow[] = visibleDriverTimesheets.map((ts) => ({
            ...ts,
            _rowType: 'driver',
        }));

        const lunchBreakRows: DisplayRow[] = visibleLunchBreaks.map((lb) => {
            const [sh, sm] = lb.start_time ? lb.start_time.split(':').map(Number) : [0, 0];
            const [eh, em] = lb.end_time ? lb.end_time.split(':').map(Number) : [0, 0];
            const durationMins = Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
            return {
                id: lb.id,
                employee: lb.employee,
                event: null as any,
                status: lb.status,
                start_date: lb.date + 'T' + (lb.start_time || '00:00:00'),
                end_date: lb.date + 'T' + (lb.end_time || '00:00:00'),
                shift_note: lb.description ?? '',
                _rowType: 'lunch_break',
                _lunchBreakDuration: durationMins,
                _lunchBreakDate: lb.date,
                _lunchBreakStartTime: lb.start_time,
                _lunchBreakEndTime: lb.end_time,
            };
        }) as DisplayRow[];

        const combined: DisplayRow[] = [...regularRows, ...driverRows, ...lunchBreakRows];

        return combined.sort((a, b) => {
            const ta = new Date(a.start_date).getTime();
            const tb = new Date(b.start_date).getTime();
            if (isNaN(ta) && isNaN(tb)) return 0;
            if (isNaN(ta)) return 1;
            if (isNaN(tb)) return -1;
            return ta - tb;
        });
    }, [visibleTimesheets, visibleDriverTimesheets, visibleLunchBreaks]);

    const rangeLabel = useMemo(() => {
        if (viewMode === "all") return "All Time Entries";

        if (viewMode === "day") return format(selectedDate, "EEE MMM d, yyyy");

        if (viewMode === "week") {
            const s = startOfWeek(selectedDate, { weekStartsOn: 1 });
            const e = endOfWeek(selectedDate, { weekStartsOn: 1 });
            return `${format(s, "MMM d")} – ${format(e, "MMM d, yyyy")}`;
        }

        return format(selectedDate, "MMMM yyyy");
    }, [viewMode, selectedDate]);

    const shiftHours = useMemo(
        () => getTotalEventHours([...visibleTimesheets, ...visibleDriverTimesheets]),
        [visibleTimesheets, visibleDriverTimesheets]
    );

    const lunchBreakHours = useMemo(
        () => visibleLunchBreaks.reduce((total, lb) => {
            const [sh, sm] = lb.start_time ? lb.start_time.split(':').map(Number) : [0, 0];
            const [eh, em] = lb.end_time ? lb.end_time.split(':').map(Number) : [0, 0];
            const mins = Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
            return total + mins / 60;
        }, 0),
        [visibleLunchBreaks]
    );

    const totalHours = useMemo(() => shiftHours - lunchBreakHours, [shiftHours, lunchBreakHours]);

    const hasDriverRows = useMemo(() => visibleDriverTimesheets.length > 0, [visibleDriverTimesheets]);
    const hideDateColumn = viewMode === "day";

    const totalLabel = useMemo(() => {
        const formatted = totalHours.toFixed(2).replace(/\.?0+$/, '');
        switch (viewMode) {
            case "day":   return `Day Total: ${formatted} hours`;
            case "week":  return `Week Total: ${formatted} hours`;
            case "month": return `Month Total: ${formatted} hours`;
            default:      return `Total: ${formatted} hours`;
        }
    }, [viewMode, totalHours]);

    const nextDisabled = useMemo(() => {
        if (viewMode === "all") return true;

        let nextAnchor = selectedDate;
        if (viewMode === "day") nextAnchor = addDays(selectedDate, 1);
        if (viewMode === "week") nextAnchor = addWeeks(selectedDate, 1);
        if (viewMode === "month") nextAnchor = addMonths(selectedDate, 1);

        return toMidnight(nextAnchor).getTime() > today.getTime();
    }, [selectedDate, viewMode, today]);


    const goPrev = () => {
        if (viewMode === "all") return;
        if (viewMode === "day") setSelectedDate(d => toMidnight(addDays(d, -1)));
        else if (viewMode === "week") setSelectedDate(d => toMidnight(addWeeks(d, -1)));
        else setSelectedDate(d => toMidnight(addMonths(d, -1)));
    };

    const goNext = () => {
        if (nextDisabled || viewMode === "all") return;
        if (viewMode === "day") setSelectedDate(d => toMidnight(addDays(d, 1)));
        else if (viewMode === "week") setSelectedDate(d => toMidnight(addWeeks(d, 1)));
        else setSelectedDate(d => toMidnight(addMonths(d, 1)));
    };

    const effectiveEmployeeId = useMemo(
        () => (selectedMember === "all" ? Number(user.userid) : Number(selectedMember)),
        [selectedMember, user.userid]
    );

    const editingKind = selectedEntryToUpdate?._rowType;
    const renderDriverForm = editingKind === 'driver' || (!editingKind && createEntryKind === 'driver');
    const selectedEmployee = useMemo(() => {
        if (selectedMember === "all") return null;
        const idStr = String(effectiveEmployeeId);
        const fromUserList = userList.find((u) => String(u.id) === idStr);
        if (fromUserList) return fromUserList;
        const fromTimesheets = timesheetList.find((ts) => String(ts.employee?.id) === idStr)?.employee;
        if (fromTimesheets) return fromTimesheets;
        const fromDriverTimesheets = driverTimesheetList.find((ts) => String(ts.employee?.id) === idStr)?.employee;
        if (fromDriverTimesheets) return fromDriverTimesheets;
        if (user.userid) {
            return {
                id: effectiveEmployeeId as any,
                first_name: user.username ?? '',
                last_name: '',
            };
        }
        return { id: effectiveEmployeeId as any };
    }, [
        selectedMember,
        effectiveEmployeeId,
        userList,
        timesheetList,
        driverTimesheetList,
        user.userid,
        user.username,
    ]);

    const defaultSelectedEmployee = useMemo(
        () => (canEditEmployee && !selectedEntryToUpdate ? selectedEmployee ?? undefined : undefined),
        [canEditEmployee, selectedEmployee, selectedEntryToUpdate]
    );

    const defaultBreakEmployeeId = useMemo(
        () => (canEditEmployee && selectedEmployee ? selectedEmployee.id : null),
        [canEditEmployee, selectedEmployee]
    );
    const selectedDateKey = useMemo(() => format(selectedDate, 'yyyy-MM-dd'), [selectedDate]);

    const columns = useMemo<ColumnDef<DisplayRow, any>[]>(() => {
        const cols: ColumnDef<DisplayRow, any>[] = [
            {
                accessorFn: row => `${row.employee?.first_name || ''} ${row.employee?.last_name || ''}`,
                id: 'full_name',
                header: 'Employee Name',
                enableColumnFilter: true,
            },
            {
                accessorFn: row =>
                    row._rowType === 'driver'
                        ? 'Driver'
                        : row._rowType === 'regular'
                            ? 'Regular'
                            : 'Break',
                id: 'entry_type',
                header: 'Type',
                enableColumnFilter: true,
                filterFn: 'arrIncludesSome',
                cell: ({ row }) => {
                    const label =
                        row.original._rowType === 'driver'
                            ? 'Driver'
                            : row.original._rowType === 'regular'
                                ? 'Regular'
                                : 'Break';
                    return row.original._rowType === 'lunch_break'
                        ? <span className="text-danger fw-semibold">{label}</span>
                        : label;
                },
            },
            {
                cell: ({ row }) => {
                    if (row.original._rowType === 'lunch_break') {
                        return 'N/A';
                    }
                    return row.original.event?.id ? (
                        <a href={`/events/${row.original.event.id}`}>
                            {row.original.event.event_name || 'Unnamed Event'}
                        </a>
                    ) : (
                        'N/A'
                    );
                },
                accessorFn: row =>
                    row._rowType === 'lunch_break'
                        ? 'N/A'
                        : (row.event?.event_name || 'N/A'),
                id: 'event_name',
                header: 'Event Name',
                enableColumnFilter: false,
            },
            {
                accessorFn: row =>
                    row._rowType === 'lunch_break'
                        ? 'N/A'
                        : (row.event?.status || 'Unknown'),
                id: 'event_status',
                header: 'Event Status',
                enableColumnFilter: true,
                filterFn: 'arrIncludesSome',
            },
            {
                accessorFn: row =>
                    row._rowType === 'lunch_break'
                        ? 'N/A'
                        : (row.CompanyName || 'N/A'),
                id: 'client_name',
                header: 'Client Name',
                enableColumnFilter: true,
            },
            {
                cell: ({ row }) => {
                    if (row.original._rowType === 'lunch_break') {
                        return <p className="text-center">N/A</p>;
                    }
                    const workOrderId =
                        typeof row.original.event?.work_order === 'object'
                            ? row.original.event.work_order?.id
                            : row.original.event?.work_order;

                    return workOrderId ? (
                        <div className="text-center">
                            {isLabourer ? (
                                <p>WO #{workOrderId}</p>
                            ) : (
                                <a href={`/workorders/${workOrderId}`}>WO #{workOrderId}</a>
                            )}
                        </div>
                    ) : (
                        <p className="text-center">N/A</p>
                    );
                },
                accessorFn: row => row._rowType === 'lunch_break' ? 'N/A' : (row.event?.work_order || 'N/A'),
                id: 'resource_workorder',
                header: 'Work Order',
                enableColumnFilter: true,
            },
        ];

        if (hasDriverRows) {
            cols.push({
                accessorFn: row => {
                    if (row._rowType === 'driver') {
                        return row.vehicle ? `${row.vehicle.make} ${row.vehicle.model}` : 'N/A';
                    }
                    return 'N/A';
                },
                id: 'vehicle',
                header: 'Vehicle',
                enableColumnFilter: true,
            });
        }

        cols.push(
            {
                accessorFn: row => row.start_date ? getDateString(row.start_date) : 'N/A',
                id: 'date',
                header: () => <span style={dateCellStyle}>Date</span>,
                cell: ({ getValue }) => <span style={dateCellStyle}>{getValue<string>()}</span>,
                enableColumnFilter: false,
            },
            {
                accessorFn: row => row.start_date ? getTimeString(row.start_date) : 'N/A',
                id: 'start_date',
                header: 'Clock In',
                enableColumnFilter: true,
                filterFn: startDateFilter,
            },
            {
                accessorFn: row => row.end_date ? getTimeString(row.end_date) : 'N/A',
                id: 'end_date',
                header: 'Clock Out',
                enableColumnFilter: true,
                filterFn: endDateFilter,
            },
            {
                accessorFn: row => row.shift_note ? row.shift_note : 'N/A',
                id: 'shift_note',
                header: 'Shift Notes',
                enableColumnFilter: false,
            },
            {
                cell: ({ row }) => {
                    if (row.original._rowType === 'lunch_break') {
                        return row.original.start_date && row.original.end_date
                            ? <span className="text-danger fw-semibold">-{getElapsedHours(row.original.start_date, row.original.end_date)}</span>
                            : <span className="text-danger fw-semibold">-0</span>;
                    }
                    return row.original.start_date && row.original.end_date
                        ? `${getElapsedHours(row.original.start_date, row.original.end_date)}`
                        : 'N/A';
                },
                accessorFn: row =>
                    row._rowType === 'lunch_break'
                        ? (row.start_date && row.end_date ? `-${getElapsedHours(row.start_date, row.end_date)}` : '-0')
                        : (row.start_date && row.end_date ? `${getElapsedHours(row.start_date, row.end_date)}` : 'N/A'),
                id: 'total_hours',
                header: 'Worked Hours',
                enableColumnFilter: false,
            },
            {
                accessorFn: row => `${row.status || 'Pending'}`,
                id: 'time_status',
                header: 'Time Entry Status',
                enableColumnFilter: true,
                filterFn: 'arrIncludesSome',
            },
            {
                id: 'actions',
                header: 'Actions',
                cell: ({ row }) => {
                    if (row.original._rowType === 'lunch_break') {
                        const lb = visibleLunchBreaks.find(l => String(l.id) === String(row.original.id));
                        if (!lb) return null;
                        const canEdit =
                            (lb.status !== 'closed' && String(lb.employee?.id) === String(user.userid)) ||
                            checkIsAllowed(user.groups, [
                                UserRoles.ownership,
                                UserRoles.management,
                                UserRoles.officeAdmin,
                            ]);
                        if (!canEdit) return null;
                        return (
                            <div className="d-flex justify-content-center">
                                <ButtonGroup className="btn-group-sm">
                                    <Button
                                        className="btn-secondary"
                                        onClick={() => {
                                            setSelectedLunchBreakToEdit(lb);
                                            setShowLunchBreakModal(true);
                                        }}
                                    >
                                        View
                                    </Button>
                                    <Button
                                        className="btn-danger"
                                        onClick={() => {
                                            setSelectedLunchBreakToDelete(lb);
                                            setShowDeleteModal(true);
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </ButtonGroup>
                            </div>
                        );
                    }

                    return (
                        <div className="d-flex flex-column align-items-center gap-1">
                            {((row.original.status !== 'closed' && row.original.employee?.id === user.userid) ||
                                checkIsAllowed(user.groups, [
                                    UserRoles.ownership,
                                    UserRoles.management,
                                    UserRoles.officeAdmin,
                                ])) ? (
                                <ButtonGroup className="btn-group-sm">
                                    <Button
                                        className="btn-secondary"
                                        onClick={() => handleOpenDetails(row.original)}
                                    >
                                        View
                                    </Button>
                                    <Button
                                        className="btn-danger"
                                        onClick={() => handleDeleteRow(row.original)}
                                    >
                                        Delete
                                    </Button>
                                </ButtonGroup>
                            ) : null}
                        </div>
                    );
                },
            },
        );

        return cols;
    }, [isLabourer, user.userid, user.groups, visibleLunchBreaks, hasDriverRows]);

    const totalFooter = useMemo(() => (
        <tfoot>
            <tr>
                <td colSpan={columns.length}>
                    <div className="d-flex justify-content-between align-items-center">
                        {/* <span className="fw-bold">{totalLabel}</span> */}
                        {viewMode === "day" && selectedMember !== "all" && (
                            <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleShowAddBreak()}
                            >
                                + Add Break
                            </Button>
                        )}
                    </div>
                </td>
            </tr>
        </tfoot>
    ), [totalLabel, columns.length, selectedMember, viewMode]);

    const handleCloseCreate = () => {
        setShowCreateModal(false);
        setSelectedEntryToDelete(null);
        setSelectedEntryToUpdate(null);
    };

    const handleCloseLunchBreak = () => {
        setShowLunchBreakModal(false);
        setSelectedLunchBreakToEdit(null);
    };

    const handleShowAddBreak = () => {
        setSelectedLunchBreakToEdit(null);
        setShowLunchBreakModal(true);
    };

    const handleShowCreate = (kind: 'regular' | 'driver') => {
        setCreateEntryKind(kind);
        setSelectedEntryToUpdate(null);
        setShowCreateModal(true);
    };

    const handleOpenDetails = (entry: DisplayRow) => {
        if (entry._rowType === 'lunch_break') return;
        const { employee, event } = entry;
        if (!employee || !event) {
            console.warn("Cannot open modal — row is missing employee or event:", entry);
            return;
        }
        setSelectedEntryToUpdate(entry);
        setShowCreateModal(true);
    };

    const handleDeleteRow = (entry: DisplayRow) => {
        setSelectedEntryToDelete(entry);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = () => {
        if (selectedLunchBreakToDelete) {
            deleteLunchBreak(String(selectedLunchBreakToDelete.id)).then(() => {
                setLunchBreakList(prev => prev.filter(lb => lb.id !== selectedLunchBreakToDelete.id));
            });
            setSelectedLunchBreakToDelete(null);
            setShowDeleteModal(false);
            return;
        }

        if (!selectedEntryToDelete) return;

        if (selectedEntryToDelete._rowType === 'driver') {
            deleteDriverTimeSheet(selectedEntryToDelete.id.toString(), selectedEntryToDelete)
                .then(() => {
                    setDriverTimesheetList(prev => prev.filter(entry => entry.id !== selectedEntryToDelete.id));
                })
                .catch((error) => console.error("Error deleting driver timesheet:", error));
        } else {
            deleteTimeSheet(selectedEntryToDelete.id.toString()).then(r => {
                if (r && !Array.isArray(r)) {
                    if (r.status >= 200 || r.status <= 300) {
                        setTimesheetList(prev => prev.filter(entry => entry.id !== selectedEntryToDelete.id));
                    }
                }
            });
        }

        setShowDeleteModal(false);
    };

    const showOffDateBanner = (startDateStr?: string) => {
        if (!startDateStr) return;
        const entryDate = toMidnight(new Date(startDateStr));
        if (entryDate.getTime() !== selectedDate.getTime()) {
            setSuccessBanner(`Time for ${format(entryDate, 'MMMM d, yyyy')} recorded successfully`);
        }
    };

    const handleCreate = (created_obj?: any) => {
        if (created_obj) {
            if (createEntryKind === 'driver') {
                setDriverTimesheetList(prev => [created_obj, ...prev]);
            } else {
                setTimesheetList(prev => [created_obj, ...prev]);
            }
            showOffDateBanner(created_obj.start_date);
        }
        setShowCreateModal(false);
    };

    const handleUpdate = (updated_obj?: TimeSheet | DriverTimeSheet) => {
        if (!selectedEntryToUpdate || !updated_obj) return;

        if (selectedEntryToUpdate._rowType === 'driver') {
            setDriverTimesheetList(prev =>
                prev.map(entry => entry.id === updated_obj.id ? (updated_obj as DriverTimeSheet) : entry)
            );
        } else if (selectedEntryToUpdate._rowType === 'regular') {
            setTimesheetList(prev =>
                prev.map(entry => entry.id === updated_obj.id ? (updated_obj as TimeSheet) : entry)
            );
        }
        showOffDateBanner((updated_obj as any).start_date);
    };

    const handleLunchBreakSaved = (lb: TimesheetLunchBreak) => {
        const employeeValue = (lb as any).employee;
        const hasEmployeeObject =
            employeeValue && typeof employeeValue === 'object' && 'id' in employeeValue;
        // When the user can edit on behalf of others, fall back to the selected member.
        // Otherwise fall back to the logged-in user so the break appears on the correct row.
        const fallbackEmployee = canEditEmployee
            ? selectedEmployee
            : (userList.find((u) => String(u.id) === String(user.userid)) ?? {
                  id: user.userid as any,
                  first_name: user.username ?? '',
                  last_name: '',
              });
        const normalizedBreak = !hasEmployeeObject && fallbackEmployee
            ? { ...lb, employee: fallbackEmployee }
            : lb;
        setLunchBreakList(prev => {
            const idx = prev.findIndex(existing => existing.id === lb.id);
            if (idx >= 0) {
                const updated = [...prev];
                updated[idx] = normalizedBreak;
                return updated;
            }
            return [normalizedBreak, ...prev];
        });
    };

    // Member selector options
    const memberOptions = useMemo(() => {
        const base = userList.map((u) => ({
            label: `${u.first_name} ${u.last_name}`,
            value: u.id,
        }));

        const withAll =
            canViewAllUsers
                ? [{ label: "All Users", value: "all" as const }, ...base]
                : base;

        if (!withAll.some(o => String(o.value) === String(user.userid))) {
            withAll.unshift({ label: "Me", value: String(user.userid) });
        }

        return withAll;
    }, [userList, canViewAllUsers, user.userid]);

    const renderAddTimeDropdown = (id: string) => (
        <Dropdown>
            <Dropdown.Toggle variant="primary" id={id}>
                Add Time
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleShowCreate('regular')}>
                    Regular Time
                </Dropdown.Item>
                {canAddDriverTime && (
                    <Dropdown.Item onClick={() => handleShowCreate('driver')}>
                        Driver Time
                    </Dropdown.Item>
                )}
                <Dropdown.Item onClick={handleShowAddBreak}>
                    Break
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );

    if (isLoading) return isLoadingSpinner();

    return (
        <div style={parentStyle}>
            <StackedContentContainer title={"Timesheet Directory"}>

                {successBanner && (
                    <Alert
                        variant="success"
                        dismissible
                        onClose={() => setSuccessBanner(null)}
                        className="mb-3"
                    >
                        {successBanner}
                    </Alert>
                )}

                {/* Top controls: Member (left) | Date picker + view mode (center) | Actions (right) */}
                <div className="mb-4">
                    <div className="row g-3 align-items-start">
                        {/* Left: Select Member */}
                        <div className="col-12 col-md-4">
                            {canViewOthers ? (
                                <>
                                    <Form.Label className="fw-bold mb-1">Select Member</Form.Label>
                                    <AsyncSelect
                                        cacheOptions
                                        defaultOptions={memberOptions}
                                        value={memberOptions.find(o => String(o.value) === String(selectedMember)) ?? null}
                                        loadOptions={(inputValue) =>
                                            Promise.resolve(
                                                memberOptions.filter((o) =>
                                                    o.label.toLowerCase().includes(inputValue.toLowerCase())
                                                )
                                            )
                                        }
                                        onChange={(opt: any) => {
                                            if (!opt) return;
                                            if (opt.value === "all" && !canViewAllUsers) return; // defensive
                                            setSelectedMember(opt.value);
                                        }}
                                        isClearable={false}
                                        placeholder="Select member..."
                                    />
                                </>
                            ) : (
                                <div />
                            )}
                        </div>

                        {/* Center: Date picker with arrows + view mode toggle underneath */}
                        <div className="col-12 col-md-4">
                            <div className="d-flex justify-content-center">
                                <div className="text-center" style={{ width: "100%", maxWidth: 320 }}>
                                    <Form.Label className="fw-bold mb-1 d-block">Date</Form.Label>

                                    {/* Arrow + DatePicker row */}
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            disabled={viewMode === "all"}
                                            onClick={goPrev}
                                        >
                                            ◀
                                        </Button>

                                        <div style={{ flex: 1 }}>
                                            <DatePicker
                                                selected={selectedDate}
                                                onChange={(d: Date | null) => {
                                                    if (!d) return;
                                                    setSelectedDate(toMidnight(d));
                                                }}
                                                dateFormat="dd-MM-yy"
                                                maxDate={today}
                                                disabled={viewMode === "all"}
                                                customInput={<ReadOnlyDateInput className="form-control text-center" />}
                                            />
                                        </div>

                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            disabled={nextDisabled}
                                            onClick={goNext}
                                        >
                                            ▶
                                        </Button>
                                    </div>

                                    {/* View mode toggle row */}
                                    <div className="d-flex justify-content-center mt-2">
                                        <ButtonGroup size="sm">
                                            <Button
                                                variant={viewMode === "day" ? "primary" : "outline-primary"}
                                                onClick={() => setViewMode("day")}
                                            >
                                                Day
                                            </Button>
                                            <Button
                                                variant={viewMode === "week" ? "primary" : "outline-primary"}
                                                onClick={() => setViewMode("week")}
                                            >
                                                Week
                                            </Button>
                                            <Button
                                                variant={viewMode === "month" ? "primary" : "outline-primary"}
                                                onClick={() => setViewMode("month")}
                                            >
                                                Month
                                            </Button>
                                            <Button
                                                variant={viewMode === "all" ? "primary" : "outline-primary"}
                                                onClick={() => setViewMode("all")}
                                            >
                                                All
                                            </Button>
                                        </ButtonGroup>
                                    </div>

                                    <div className="mt-1">
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0"
                                            disabled={toMidnight(selectedDate).getTime() === today.getTime()}
                                            onClick={() => setSelectedDate(today)}
                                        >
                                            Today
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="col-12 col-md-4">
                            <div className="d-flex justify-content-md-end justify-content-start">
                                {checkIsAllowed(user.groups, [
                                    UserRoles.ownership,
                                    UserRoles.management,
                                    UserRoles.officeAdmin,
                                    UserRoles.sales,
                                ]) ? (
                                    <div className="d-flex flex-wrap gap-2">
                                        <Button variant={'success'} onClick={() => setShowExportModal(true)}>
                                            Export Timesheet
                                        </Button>
                                        {renderAddTimeDropdown("add-time-dropdown")}
                                    </div>
                                ) : (
                                    <div className="d-flex flex-wrap gap-2">
                                        {renderAddTimeDropdown("add-time-dropdown-basic")}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>



                {/* Content: empty state or table */}
                {allDisplayRows.length === 0 ? (
                    <div className="text-center py-4">
                        <h5 className="mb-2">
                            No time entries{viewMode === "all" ? "" : ` for ${rangeLabel}`}
                        </h5>
                        <p className="text-muted mb-3">
                            Click below to enter time{viewMode === "all" ? "" : " for this period"}.
                        </p>
                        <div className="d-flex justify-content-center flex-wrap gap-2">
                            {renderAddTimeDropdown("add-time-dropdown-empty")}
                        </div>
                    </div>
                ) : (
                    <SearchableTable
                        columns={columns}
                        data={allDisplayRows}
                        globalSearch={true}
                        customFooter={totalFooter}
                        hiddenColumnIds={hideDateColumn ? ['date', 'shift_note'] : ['shift_note']}
                    />
                )}
            </StackedContentContainer>

            {/* Create / Update */}
            <Offcanvas
                show={showCreateModal}
                onHide={handleCloseCreate}
                placement="end"
                backdrop={true}
                scroll={false}
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        {selectedEntryToUpdate
                            ? (renderDriverForm ? "Update Driver Time" : "Update Time")
                            : (renderDriverForm ? "Add Driver Time" : "Add Regular Time")
                        }
                    </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    <CreateTimeEntry
                        key={selectedEntryToUpdate ? `edit-${selectedEntryToUpdate.id}` : `create-${String(showCreateModal)}-${renderDriverForm ? 'driver' : 'regular'}`}
                        timeEntryToUpdate={
                            selectedEntryToUpdate && selectedEntryToUpdate._rowType === 'regular'
                                ? selectedEntryToUpdate
                                : undefined
                        }
                        driverTimeEntryToUpdate={
                            selectedEntryToUpdate && selectedEntryToUpdate._rowType === 'driver'
                                ? selectedEntryToUpdate
                                : undefined
                        }
                        renderDriver={renderDriverForm}
                        closeModal={handleCloseCreate}
                        onCreate={handleCreate}
                        onUpdate={handleUpdate}
                        defaultWorkDate={selectedDate}
                        selectedEmployee={defaultSelectedEmployee}
                    />
                </Offcanvas.Body>
            </Offcanvas>


            {/* Export */}
            <Modal show={showExportModal} onHide={() => {setShowExportModal(false)}} size={"lg"}>
                <Modal.Header closeButton>
                    <Modal.Title>Export Time</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ExportTimecard
                        endpoint={'timesheet-combined-csv'}
                        workorderList={workOrderList}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {setShowExportModal(false)}}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete */}
            <DeleteConfirmationModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setSelectedLunchBreakToDelete(null);
                    setSelectedEntryToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
            />

            {/* Add / Edit Lunch Break */}
            <AddLunchBreakModal
                show={showLunchBreakModal}
                onHide={handleCloseLunchBreak}
                employeeId={user.userid ?? ''}
                defaultDate={selectedDate}
                lunchBreakToEdit={selectedLunchBreakToEdit}
                onSaved={handleLunchBreakSaved}
                canEditEmployee={canEditEmployee}
                employeeOptions={userList}
                defaultEmployeeId={defaultBreakEmployeeId}
            />
        </div>
    );
}

export default ManageTimeEntriesPage;
